import { useAtomValue } from "jotai";
import { useEffect } from "react";
import { graphql, readInlineData } from "react-relay";

import { useCurrentPageType } from "shared/hooks";
import { PageType } from "shared/lib/page-type";

import { currentArticleAtom } from "scmp-app/lib/current-item";
import { useIabCategory } from "scmp-app/lib/hooks";
import type { hooksUseCurrentArticlePrebidConfigArticle$key } from "scmp-app/queries/__generated__/hooksUseCurrentArticlePrebidConfigArticle.graphql";

// cspell: ignore ortb pagetype pagecat sectioncat
declare global {
  interface Window {
    pbjs: {
      que: Array<() => void>;
      setConfig: (config: {
        ortb2: {
          site: {
            content: {
              data: [
                {
                  ext: { segtax: 7 };
                  name: "iab-content-taxonomy-v3";
                  segment: { id: string }[];
                },
              ];
            };
            keywords: string;
          };
        };
      }) => void;
    };
  }
}

export const useCurrentArticlePrebidConfig = () => {
  const currentArticle = useAtomValue(currentArticleAtom);
  const currentPageType = useCurrentPageType();
  const { iabCategoryAppConfigState } = useIabCategory();

  const article = readInlineData<hooksUseCurrentArticlePrebidConfigArticle$key>(
    graphql`
      fragment hooksUseCurrentArticlePrebidConfigArticle on Article @inline {
        sections {
          value {
            entityId
            name
          }
        }
        topics {
          name
        }
      }
    `,
    currentArticle,
  );

  useEffect(() => {
    if (currentPageType !== PageType.Article) return;

    const segment: { id: string }[] = [];
    const sectionNames: string[] = [];

    for (const item of article?.sections?.[0]?.value ?? []) {
      if (item?.entityId && iabCategoryAppConfigState?.[item.entityId]) {
        segment.push({
          id: iabCategoryAppConfigState[item.entityId],
        });
      }
      if (item?.name) {
        sectionNames.push(item.name);
      }
    }

    const topicNames = article?.topics?.map(topic => topic?.name ?? "") ?? [];
    const keywords = [...sectionNames, ...topicNames]
      .filter(keyword => keyword.trim() !== "")
      .join(", ");

    window.pbjs = window.pbjs ?? { que: [], setConfig: () => {} };
    window.pbjs.que = window.pbjs.que ?? [];
    window.pbjs.que.push(() => {
      window.pbjs.setConfig({
        ortb2: {
          site: {
            content: {
              data: [
                {
                  ext: { segtax: 7 },
                  name: "iab-content-taxonomy-v3",
                  segment,
                },
              ],
            },
            keywords,
          },
        },
      });
    });
  }, [currentPageType, article, iabCategoryAppConfigState]);
};
